"use client";

import { X } from "lucide-react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ModelSelector } from "@/components/model-selector";
import { AuthDialog } from "@/components/playground/auth-dialog";
import { ChatSidebar } from "@/components/playground/chat-sidebar";
import { GroupChatUI } from "@/components/playground/group-chat-ui";
import { SidebarProvider } from "@/components/ui/sidebar";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useUser } from "@/hooks/useUser";
import { getContextualChatErrorMessage } from "@/lib/chat-error";
import { extractTextFromUiMessage, isAbortLikeError, streamGroupChatResponse, type GroupUiMessage } from "@/lib/group-chat-stream";
import { mapModels } from "@/lib/mapmodels";
import { shouldDisableFallback } from "@/lib/no-fallback";
import { useI18n } from "@llmgateway/i18n/react";
import { getProviderIcon } from "@llmgateway/shared/components";
import type { ApiModel, ApiProvider } from "@/lib/fetch-models";
import type { GroupChatMode, GroupMessage } from "@/lib/group-chat-types";
import type { ComboboxModel, Organization, Project } from "@/lib/types";
interface GroupChatClientProps {
  models: ApiModel[];
  providers: ApiProvider[];
  organizations: Organization[];
  selectedOrganization: Organization | null;
  projects: Project[];
  selectedProject: Project | null;
}
export default function GroupChatClient({
  models,
  providers,
  organizations,
  selectedOrganization,
  projects,
  selectedProject
}: GroupChatClientProps) {
  const {
    t
  } = useI18n();
  const {
    user,
    isLoading: isUserLoading
  } = useUser();
  const posthog = usePostHog();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Filter out image/video/embedding models — group chat is text-only
  const chatModels = useMemo(() => models.filter(m => !m.output?.includes("image") && !m.output?.includes("video") && !m.output?.includes("embedding")), [models]);
  const mapped = useMemo(() => mapModels(chatModels, providers), [chatModels, providers]);
  const [availableModels] = useState<ComboboxModel[]>(mapped);
  const [mode, setMode] = useState<GroupChatMode>("answer");
  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const [messages, setMessages] = useState<GroupMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentChatId] = useState<string | null>(null);
  const [initialPrompt, setInitialPrompt] = useState("");
  const maxTurns = 5; // Limit conversation length
  const turnCounterRef = useRef(0);
  const tempMessageIdRef = useRef<string | null>(null);
  const lastAssistantTextRef = useRef<string>("");
  const stoppedRef = useRef<boolean>(false);
  const messagesRef = useRef<GroupMessage[]>([]);
  const requestMessagesRef = useRef<GroupUiMessage[]>([]);
  const abortControllersRef = useRef(new Set<AbortController>());
  const advanceScheduledRef = useRef<boolean>(false);
  const runIdRef = useRef(0);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);
  const setGroupMessages = useCallback((updater: GroupMessage[] | ((previous: GroupMessage[]) => GroupMessage[])) => {
    setMessages(previous => {
      const nextMessages = typeof updater === "function" ? updater(previous) : updater;
      messagesRef.current = nextMessages;
      return nextMessages;
    });
  }, []);
  const isAuthenticated = !isUserLoading && !!user;
  const showAuthDialog = !isAuthenticated && !isUserLoading && !user;
  const chatOrganization = useMemo(() => organizations.find(organization => organization.isChat) ?? null, [organizations]);
  const returnUrl = useMemo(() => {
    const search = searchParams.toString();
    return search ? `${pathname}?${search}` : pathname;
  }, [pathname, searchParams]);
  const ensuredProjectRef = useRef<string | null>(null);

  // Ensure playground key exists
  useEffect(() => {
    if (!isAuthenticated || !selectedProject) {
      ensuredProjectRef.current = null;
      return;
    }
    const ensureKey = async () => {
      if (!selectedOrganization) {
        return;
      }
      const projectId = selectedProject.id;
      if (ensuredProjectRef.current === projectId) {
        return;
      }
      try {
        const response = await fetch("/api/ensure-playground-key", {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            projectId
          })
        });
        if (response.ok && selectedProject.id === projectId) {
          ensuredProjectRef.current = projectId;
        }
      } catch {
        // ignore for now
      }
    };
    void ensureKey();
  }, [isAuthenticated, selectedOrganization, selectedProject]);
  const addModel = (modelId: string) => {
    if (selectedModels.length < 5 && !selectedModels.includes(modelId)) {
      setSelectedModels([...selectedModels, modelId]);
    }
  };
  const removeModel = (modelId_0: string) => {
    setSelectedModels(selectedModels.filter(m_0 => m_0 !== modelId_0));
  };
  const abortActiveRequests = useCallback(() => {
    for (const controller of Array.from(abortControllersRef.current)) {
      controller.abort();
    }
    abortControllersRef.current.clear();
  }, []);
  const resetConversationState = useCallback(({
    preservePrompt = false
  }: {
    preservePrompt?: boolean;
  } = {}) => {
    abortActiveRequests();
    runIdRef.current += 1;
    setGroupMessages([]);
    requestMessagesRef.current = [];
    turnCounterRef.current = 0;
    tempMessageIdRef.current = null;
    lastAssistantTextRef.current = "";
    stoppedRef.current = false;
    advanceScheduledRef.current = false;
    setError(null);
    setIsStreaming(false);
    if (!preservePrompt) {
      setInitialPrompt("");
    }
  }, [abortActiveRequests, setGroupMessages]);
  const updateAssistantMessage = useCallback((messageId: string, content: string) => {
    setGroupMessages(previous_0 => previous_0.map(message => message.id === messageId ? {
      ...message,
      content
    } : message));
  }, [setGroupMessages]);
  const registerAbortController = useCallback(() => {
    const controller_0 = new AbortController();
    abortControllersRef.current.add(controller_0);
    return controller_0;
  }, []);
  const buildErrorMessage = useCallback((rawError: unknown) => getContextualChatErrorMessage({
    error: rawError,
    organizations,
    selectedOrganization,
    chatOrganization,
    t
  }), [chatOrganization, organizations, selectedOrganization, t]);

  // Helper to safely schedule advancing to the next turn exactly once
  const scheduleNextTurn = () => {
    if (advanceScheduledRef.current) {
      return;
    }
    advanceScheduledRef.current = true;
    const nextTurn = turnCounterRef.current + 1;
    turnCounterRef.current = nextTurn;
    if (!stoppedRef.current && nextTurn < maxTurns) {
      setTimeout(() => {
        void continueDebateConversation(nextTurn);
      }, 60);
    } else {
      setIsStreaming(false);
    }
  };
  const startAnswering = async () => {
    if (!initialPrompt.trim() || selectedModels.length < 2) {
      setError(t("groupChat.missingPromptOrModels"));
      return;
    }
    resetConversationState({
      preservePrompt: true
    });
    const activeRunId = runIdRef.current;
    setError(null);
    setIsStreaming(true);
    stoppedRef.current = false;
    posthog.capture("playground_group_chat_started", {
      mode,
      models: selectedModels,
      model_count: selectedModels.length
    });
    const now = Date.now();
    const userMessage: GroupMessage = {
      id: crypto.randomUUID(),
      role: "user",
      content: initialPrompt,
      timestamp: now
    };
    const tempMessages = selectedModels.map((modelId_1, index) => ({
      id: crypto.randomUUID(),
      role: "assistant" as const,
      content: "",
      model: modelId_1,
      timestamp: now + index
    }));
    const nextMessages_0 = [userMessage, ...tempMessages];
    setGroupMessages(nextMessages_0);
    const userUiMessage: GroupUiMessage = {
      id: crypto.randomUUID(),
      role: "user",
      parts: [{
        type: "text",
        text: initialPrompt
      }]
    };
    requestMessagesRef.current = [userUiMessage];
    const runModel = async (modelId_2: string, tempMessageId: string): Promise<string | null> => {
      const controller_1 = registerAbortController();
      const noFallback = shouldDisableFallback(modelId_2);
      try {
        await streamGroupChatResponse({
          abortSignal: controller_1.signal,
          messages: [userUiMessage],
          model: modelId_2,
          noFallback,
          onTextUpdate: ({
            text
          }) => {
            if (runIdRef.current !== activeRunId) {
              return;
            }
            updateAssistantMessage(tempMessageId, text);
          }
        });
        return null;
      } catch (streamError) {
        if (runIdRef.current !== activeRunId || controller_1.signal.aborted || isAbortLikeError(streamError)) {
          return null;
        }
        const errorMessage = buildErrorMessage(streamError);
        updateAssistantMessage(tempMessageId, errorMessage);
        return errorMessage;
      } finally {
        abortControllersRef.current.delete(controller_1);
      }
    };
    const results = await Promise.allSettled(tempMessages.map(message_0 => runModel(message_0.model!, message_0.id)));
    if (runIdRef.current === activeRunId && !stoppedRef.current) {
      const lastError = [...results].reverse().find((result): result is PromiseFulfilledResult<string | null> => result.status === "fulfilled" && typeof result.value === "string")?.value;
      setError(lastError ?? null);
      setIsStreaming(false);
    }
  };
  const continueDebateConversation = async (currentTurn: number) => {
    if (stoppedRef.current || currentTurn >= maxTurns || selectedModels.length === 0) {
      return;
    }
    setError(null);
    setIsStreaming(true);
    const activeRunId_0 = runIdRef.current;
    advanceScheduledRef.current = false; // allow one advancement for this turn
    const modelIndex = currentTurn % selectedModels.length;
    const currentModel = selectedModels[modelIndex];

    // Create a temporary group assistant message for streaming
    const tempMessageId_0 = crypto.randomUUID();
    tempMessageIdRef.current = tempMessageId_0;
    const tempMessage: GroupMessage = {
      id: tempMessageId_0,
      role: "assistant",
      content: "",
      model: currentModel,
      timestamp: Date.now()
    };
    setGroupMessages(previous_1 => [...previous_1, tempMessage]);

    // Determine the input for this turn and prepend debate instruction
    const turnInput = currentTurn === 0 ? initialPrompt : lastAssistantTextRef.current;
    const debateInstruction = currentTurn === 0 ? `Debate mode: Take a clear stance and present a concise argument on: "${initialPrompt}". Do not argue both sides.` : `Argue the opposing side to the previous response. Previous response: "${lastAssistantTextRef.current}". Provide a concise counter-argument.`;
    const userUiMessage_0: GroupUiMessage = {
      id: crypto.randomUUID(),
      role: "user",
      parts: [{
        type: "text",
        text: debateInstruction
      }, {
        type: "text",
        text: "\n\n" + turnInput
      }]
    };
    const outboundMessages = [...requestMessagesRef.current, userUiMessage_0];
    const noFallback_0 = shouldDisableFallback(currentModel);
    const controller_2 = registerAbortController();
    try {
      const finalAssistantMessage = await streamGroupChatResponse({
        abortSignal: controller_2.signal,
        messages: outboundMessages,
        model: currentModel,
        noFallback: noFallback_0,
        onTextUpdate: ({
          text: text_0
        }) => {
          if (runIdRef.current !== activeRunId_0) {
            return;
          }
          lastAssistantTextRef.current = text_0;
          updateAssistantMessage(tempMessageId_0, text_0);
        }
      });
      if (runIdRef.current !== activeRunId_0 || controller_2.signal.aborted || stoppedRef.current) {
        return;
      }
      const finalText = extractTextFromUiMessage(finalAssistantMessage);
      lastAssistantTextRef.current = finalText;
      requestMessagesRef.current = [...outboundMessages, finalAssistantMessage ?? {
        id: crypto.randomUUID(),
        role: "assistant",
        parts: [{
          type: "text",
          text: finalText,
          state: "done"
        }]
      }];
      // Advance on successful completion of this turn
      scheduleNextTurn();
    } catch (err) {
      if (runIdRef.current !== activeRunId_0 || controller_2.signal.aborted || isAbortLikeError(err)) {
        return;
      }
      const messageText = buildErrorMessage(err);
      setError(messageText);
      // Reflect error in the temp assistant message and proceed
      if (tempMessageIdRef.current) {
        updateAssistantMessage(tempMessageIdRef.current, messagesRef.current.find(message_1 => message_1.id === tempMessageIdRef.current)?.content || messageText);
        const partial = messagesRef.current.find(m_1 => m_1.id === tempMessageIdRef.current)?.content;
        const nextAssistantText = partial && typeof partial === "string" ? partial : messageText;
        lastAssistantTextRef.current = nextAssistantText;
        requestMessagesRef.current = [...outboundMessages, {
          id: crypto.randomUUID(),
          role: "assistant",
          parts: [{
            type: "text",
            text: nextAssistantText,
            state: "done"
          }]
        }];
      }
      scheduleNextTurn();
    } finally {
      abortControllersRef.current.delete(controller_2);
    }
  };
  const startDebating = async () => {
    if (!initialPrompt.trim() || selectedModels.length < 2) {
      setError(t("groupChat.missingPromptOrModels"));
      return;
    }
    setError(null);
    stoppedRef.current = false;
    posthog.capture("playground_group_chat_started", {
      mode,
      models: selectedModels,
      model_count: selectedModels.length
    });
    if (messages.length === 0) {
      resetConversationState({
        preservePrompt: true
      });
      const userMessage_0: GroupMessage = {
        id: crypto.randomUUID(),
        role: "user",
        content: initialPrompt,
        timestamp: Date.now()
      };
      setGroupMessages([userMessage_0]);
      requestMessagesRef.current = [];
      await continueDebateConversation(0);
      return;
    }
    await continueDebateConversation(turnCounterRef.current);
  };
  const startConversation = async () => {
    if (mode === "answer") {
      await startAnswering();
      return;
    }
    await startDebating();
  };
  const stopConversation = () => {
    abortActiveRequests();
    runIdRef.current += 1;
    setIsStreaming(false);
    turnCounterRef.current = maxTurns; // Stop the conversation
    stoppedRef.current = true;
  };
  const clearConversation = () => {
    resetConversationState();
  };
  const handleModeChange = (nextMode: string) => {
    if (nextMode !== "answer" && nextMode !== "debate") {
      return;
    }
    if (nextMode === mode) {
      return;
    }
    resetConversationState({
      preservePrompt: true
    });
    setMode(nextMode);
  };
  const handleSelectOrganization = (org: Organization | null) => {
    const params = new URLSearchParams(Array.from(searchParams.entries()));
    if (org?.id) {
      params.set("orgId", org.id);
    } else {
      params.delete("orgId");
    }
    params.delete("projectId");
    router.push(params.toString() ? `/group?${params.toString()}` : "/group");
  };
  const handleOrganizationCreated = (org_0: Organization) => {
    const params_0 = new URLSearchParams(Array.from(searchParams.entries()));
    params_0.set("orgId", org_0.id);
    params_0.delete("projectId");
    router.push(params_0.toString() ? `/group?${params_0.toString()}` : "/group");
  };
  const handleSelectProject = (project: Project | null) => {
    if (!project) {
      return;
    }
    const params_1 = new URLSearchParams(Array.from(searchParams.entries()));
    params_1.set("orgId", project.organizationId);
    params_1.set("projectId", project.id);
    router.push(params_1.toString() ? `/group?${params_1.toString()}` : "/group");
  };
  const handleProjectCreated = (project_0: Project) => {
    const params_2 = new URLSearchParams(Array.from(searchParams.entries()));
    params_2.set("orgId", project_0.organizationId);
    params_2.set("projectId", project_0.id);
    router.push(params_2.toString() ? `/group?${params_2.toString()}` : "/group");
  };
  return <SidebarProvider>
			<div className="flex h-svh bg-background w-full overflow-hidden">
				<ChatSidebar onNewChat={clearConversation} onChatSelect={() => {}} currentChatId={currentChatId ?? undefined} isLoading={isStreaming} organizations={organizations} selectedOrganization={selectedOrganization} onSelectOrganization={handleSelectOrganization} onOrganizationCreated={handleOrganizationCreated} projects={projects} selectedProject={selectedProject} onSelectProject={handleSelectProject} onProjectCreated={handleProjectCreated} />
				<div className="flex flex-1 flex-col w-full min-h-0 overflow-hidden">
					<header className="shrink-0 flex items-center p-4 border-b bg-background">
						<div className="flex items-center gap-3 min-w-0 flex-1">
							<SidebarTrigger />
							<div className="flex items-center gap-2 flex-1 min-w-0">
								<h1 className="text-lg font-semibold whitespace-nowrap">
									{t("playground.groupChat")}
								</h1>
							</div>
						</div>
					</header>

					<div className="flex-1 flex flex-col min-h-0 w-full max-w-[1800px] mx-auto overflow-hidden">
						{/* Model Selection Panel */}
						<div className="shrink-0 p-4 border-b bg-muted/30">
							<div className="space-y-3">
								<div>
									<h2 className="text-sm font-medium mb-4">
										{t("groupChat.selectedModels", {
                    count: selectedModels.length
                  })}
									</h2>
									<div className="flex flex-col gap-3 lg:flex-row lg:items-start">
										<div className="min-w-0 flex-1">
											{selectedModels.length < 5 && <ModelSelector models={chatModels} providers={providers} value="" onValueChange={value => {
                      if (value) {
                        addModel(value);
                      }
                    }} placeholder={t("modelSelector.addModel")} />}
										</div>
										<Tabs value={mode} onValueChange={handleModeChange} className="gap-0">
											<TabsList className="h-10">
												<TabsTrigger value="answer" className="px-4">
													{t("groupChat.modeAnswer")}
												</TabsTrigger>
												<TabsTrigger value="debate" className="px-4">
													{t("groupChat.modeDebate")}
												</TabsTrigger>
											</TabsList>
										</Tabs>
									</div>
								</div>

								{selectedModels.length > 0 && <div className="flex flex-wrap gap-2">
										{selectedModels.map(modelId_3 => {
                  const model = availableModels.find(m_2 => m_2.id === modelId_3);
                  const providerDef = providers.find(p => p.id === model?.providerId as any);
                  const ProviderIcon = providerDef ? getProviderIcon(providerDef.id) : null;
                  const bgTint = providerDef?.color ? `${providerDef.color}15` : undefined;
                  const borderTint = providerDef?.color ? `${providerDef.color}30` : undefined;
                  return <div key={modelId_3} className="flex items-center gap-2 pl-2 pr-1 py-1.5 border rounded-full text-sm" style={{
                    backgroundColor: bgTint,
                    borderColor: borderTint
                  }}>
													{ProviderIcon ? <ProviderIcon className="h-3.5 w-3.5" style={{
                      color: providerDef?.color ?? undefined
                    }} /> : null}
													<span className="max-w-[220px] truncate">
														{model?.id ?? modelId_3}
													</span>
													<button type="button" onClick={() => removeModel(modelId_3)} className="rounded-full hover:bg-foreground/10 transition-colors p-1" aria-label={t("groupChat.removeModel", {
                      model: model?.id ?? modelId_3
                    })}>
														<X className="size-3" />
													</button>
												</div>;
                })}
									</div>}

								{selectedModels.length === 0 && <p className="text-sm text-muted-foreground">
										{t("groupChat.addAtLeastTwoModels")}
									</p>}
							</div>
						</div>

						<GroupChatUI mode={mode} messages={messages} isStreaming={isStreaming} error={error} initialPrompt={initialPrompt} setInitialPrompt={setInitialPrompt} onStart={startConversation} onStop={stopConversation} onClear={clearConversation} selectedModels={selectedModels} availableModels={availableModels} canStart={selectedModels.length >= 2 && !isStreaming} />
					</div>
				</div>
			</div>
			<AuthDialog open={showAuthDialog} returnUrl={returnUrl} />
		</SidebarProvider>;
}